import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useBatch } from '@/contexts/BatchContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Plus,
  Search,
  Upload,
  Download,
  Edit,
  Trash,
  Eye,
  UserX,
  Trash2,
  Mail,
  Loader2,
  Key,
  ShieldOff,
  ShieldCheck,
  UserCheck,
  Archive
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { CardLoadingSpinner } from '@/components/ui/loading-spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { Student } from '@/types';
import { studentApi } from '@/lib/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const StudentsPage = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(20);
  const [sendingEmailTo, setSendingEmailTo] = useState<string | null>(null);
  const [resettingPasswordFor, setResettingPasswordFor] = useState<string | null>(null);
  const { toast } = useToast();

  // Use batch context for all batch-related functionality
  const { currentBatch, hasBatches, loading: batchLoading } = useBatch();

  // Function to fetch students that can be called from anywhere
  const fetchStudents = useCallback(async (page = 0, size = pageSize) => {
    // Don't fetch if batch context is still loading
    if (batchLoading) {
      console.log('Batch context is still loading, skipping student fetch');
      return;
    }

    // Use current batch
    const batchId = currentBatch?.id;

    if (!batchId) {
      console.log('No batch available, cannot fetch students');
      setError('No batch selected. Please select a batch to view students.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log('Fetching students for batch:', batchId, 'page:', page, 'size:', size);
      const response = await studentApi.getAllPaginated(batchId, page, size);
      console.log('Students response:', response);

      // Handle paginated response
      if (response && typeof response === 'object' && response.content) {
        setStudents(Array.isArray(response.content) ? response.content : []);
        setCurrentPage(response.number || 0);
        setTotalPages(response.totalPages || 0);
        setTotalElements(response.totalElements || 0);
      } else {
        // Fallback for non-paginated response
        const data = Array.isArray(response) ? response : [];
        setStudents(data);
        setCurrentPage(0);
        setTotalPages(1);
        setTotalElements(data.length);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to load students. Please try again.');
      setStudents([]);
      setCurrentPage(0);
      setTotalPages(0);
      setTotalElements(0);
      toast({
        title: 'Error',
        description: 'Failed to load students',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [currentBatch?.id, batchLoading, toast, pageSize]);

  // Fetch students when the component mounts or when the current batch/year changes
  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  // Check for refresh parameter in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('refresh') === 'true') {
      console.log('Refresh parameter detected, fetching students again');
      fetchStudents();

      // Remove the refresh parameter from the URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('refresh');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [location.search]);

  // Filter students based on search term - ensure students is always an array
  const filteredStudents = (Array.isArray(students) ? students : []).filter(student =>
    student.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.studentId.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteStudent = async () => {
    toast({
      title: 'Delete Not Allowed',
      description: 'Students cannot be deleted. Please use "Revoke Access" for fee issues or "Deactivate" if the student leaves.',
      variant: 'destructive',
    });
  };

  const handleRevokeAccess = async (id: string) => {
    const reason = prompt('Reason for revoking access (e.g., Fee payment pending):');
    if (reason === null) return; // User cancelled

    try {
      const updatedStudent = await studentApi.revokeAccess(id, reason || 'Fee payment pending');
      setStudents((Array.isArray(students) ? students : []).map(student =>
        student.id === id ? updatedStudent : student
      ));
      toast({
        title: 'Success',
        description: 'Student access revoked. They can login but cannot access tests/analytics.',
      });
    } catch (err) {
      console.error('Error revoking access:', err);
      toast({
        title: 'Error',
        description: 'Failed to revoke access',
        variant: 'destructive',
      });
    }
  };

  const handleRestoreAccess = async (id: string) => {
    if (!confirm('Restore student access? They will be able to access all features.')) {
      return;
    }

    try {
      const updatedStudent = await studentApi.restoreAccess(id);
      setStudents((Array.isArray(students) ? students : []).map(student =>
        student.id === id ? updatedStudent : student
      ));
      toast({
        title: 'Success',
        description: 'Student access restored successfully',
      });
    } catch (err) {
      console.error('Error restoring access:', err);
      toast({
        title: 'Error',
        description: 'Failed to restore access',
        variant: 'destructive',
      });
    }
  };

  const handleDeactivateStudent = async (id: string) => {
    const reason = prompt('Reason for deactivating student (e.g., Left college, Transferred):');
    if (reason === null) return; // User cancelled

    if (!confirm('Deactivate this student account? They will not be able to login until reactivated.')) {
      return;
    }

    try {
      const updatedStudent = await studentApi.deactivateAccount(id, reason || 'Left college');
      setStudents((Array.isArray(students) ? students : []).map(student =>
        student.id === id ? updatedStudent : student
      ));
      toast({
        title: 'Success',
        description: 'Student account deactivated successfully',
      });
    } catch (err) {
      console.error('Error deactivating student:', err);
      toast({
        title: 'Error',
        description: 'Failed to deactivate student',
        variant: 'destructive',
      });
    }
  };

  const handleReactivateStudent = async (id: string) => {
    if (!confirm('Reactivate this student account? They will be able to login again.')) {
      return;
    }

    try {
      const updatedStudent = await studentApi.reactivateAccount(id);
      setStudents((Array.isArray(students) ? students : []).map(student =>
        student.id === id ? updatedStudent : student
      ));
      toast({
        title: 'Success',
        description: 'Student account reactivated successfully',
      });
    } catch (err) {
      console.error('Error reactivating student:', err);
      toast({
        title: 'Error',
        description: 'Failed to reactivate student',
        variant: 'destructive',
      });
    }
  };

  const handleArchiveStudent = async (id: string) => {
    if (!confirm('Archive this student? This should only be done 6 months after education completion.')) {
      return;
    }

    try {
      const updatedStudent = await studentApi.archive(id);
      setStudents((Array.isArray(students) ? students : []).map(student =>
        student.id === id ? updatedStudent : student
      ));
      toast({
        title: 'Success',
        description: 'Student archived successfully',
      });
    } catch (err) {
      console.error('Error archiving student:', err);
      toast({
        title: 'Error',
        description: 'Failed to archive student',
        variant: 'destructive',
      });
    }
  };

  const handleBulkDelete = async () => {
    if (selectedStudents.length === 0) {
      toast({
        title: 'No Selection',
        description: 'Please select students to delete',
        variant: 'destructive',
      });
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedStudents.length} student(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      await studentApi.bulkDelete(selectedStudents);
      setStudents((Array.isArray(students) ? students : []).filter(student => !selectedStudents.includes(student.id)));
      setSelectedStudents([]);
      toast({
        title: 'Success',
        description: `${selectedStudents.length} student(s) deleted successfully`,
      });
    } catch (err) {
      console.error('Error bulk deleting students:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete selected students',
        variant: 'destructive',
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedStudents(filteredStudents.map(student => student.id));
    } else {
      setSelectedStudents([]);
    }
  };

  const handleSelectStudent = (studentId: string, checked: boolean) => {
    if (checked) {
      setSelectedStudents([...selectedStudents, studentId]);
    } else {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    }
  };

  const handleResendWelcomeEmail = async (studentId: string) => {
    setSendingEmailTo(studentId);
    try {
      const response = await studentApi.resendWelcomeEmail(studentId);

      if (response.success) {
        toast({
          title: 'Success',
          description: response.message,
        });
      } else {
        toast({
          title: 'Error',
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error resending welcome email:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to resend welcome email. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSendingEmailTo(null);
    }
  };

  const handleResetPassword = async (studentId: string) => {
    if (!confirm('Are you sure you want to reset this student\'s password? A password reset email will be sent to the student.')) {
      return;
    }

    setResettingPasswordFor(studentId);
    try {
      const response = await studentApi.resetPassword(studentId);

      if (response.success) {
        toast({
          title: 'Success',
          description: response.message,
        });
      } else {
        toast({
          title: 'Error',
          description: response.message,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      console.error('Error resetting password:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to reset password. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setResettingPasswordFor(null);
    }
  };

  // Show loading state while batch context is loading
  if (batchLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">Students</h1>
        </div>
        <Card>
          <CardContent>
            <CardLoadingSpinner message="Loading batch information..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state when no batches are available
  if (!hasBatches) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold tracking-tight">Students</h1>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <p className="text-lg font-medium text-gray-900 mb-2">No Batches Available</p>
            <p className="text-gray-600 mb-4 text-center">
              You need to create a batch before you can manage students.
              A batch represents a period for organizing students.
            </p>
            <Button asChild>
              <Link to="/settings/batches">
                <Plus className="mr-2 h-4 w-4" />
                Create Batch
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold tracking-tight">Students</h1>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Student Management</CardTitle>
          <div className="flex space-x-2">
            <Button asChild>
              <Link to="/students/new">
                <Plus className="mr-2 h-4 w-4" />
                Add Student
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/students/import">
                <Upload className="mr-2 h-4 w-4" />
                Import
              </Link>
            </Button>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between py-4">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search students..."
                className="pl-8"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            {selectedStudents.length > 0 && (
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">
                  {selectedStudents.length} selected
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Selected
                </Button>
              </div>
            )}
          </div>
          {loading ? (
            <CardLoadingSpinner message="Loading students..." />
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={() => window.location.reload()}>Retry</Button>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={selectedStudents.length === filteredStudents.length && filteredStudents.length > 0}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all students"
                      />
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Student ID</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Enrollment Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Classes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStudents.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="h-24 text-center">
                        No students found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStudents.map((student) => (
                      <TableRow key={student.id}>
                        <TableCell>
                          <Checkbox
                            checked={selectedStudents.includes(student.id)}
                            onCheckedChange={(checked: boolean) => handleSelectStudent(student.id, checked)}
                            aria-label={`Select ${student.firstName} ${student.lastName}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {student.firstName} {student.lastName}
                        </TableCell>
                        <TableCell>{student.studentId}</TableCell>
                        <TableCell>{student.email}</TableCell>
                        <TableCell>{student.enrollmentDate}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <StatusBadge status={student.status} variant="rounded" />
                            {student.accessRevoked && (
                              <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700">
                                Access Revoked
                              </span>
                            )}
                            {student.isActive === false && (
                              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700">
                                Deactivated
                              </span>
                            )}
                            {student.isArchived && (
                              <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-1 text-xs font-medium text-purple-700">
                                Archived
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{student.classIds?.length || 0}</TableCell>
                        <TableCell className="text-right">
                          <TooltipProvider>
                            <div className="flex items-center justify-end space-x-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    asChild
                                    className="h-8 w-8 p-0"
                                  >
                                    <Link to={`/students/${student.id}`}>
                                      <Eye className="h-4 w-4" />
                                      <span className="sr-only">View Details</span>
                                    </Link>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>View Student Details</p>
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    asChild
                                    className="h-8 w-8 p-0"
                                  >
                                    <Link to={`/students/${student.id}/edit`}>
                                      <Edit className="h-4 w-4" />
                                      <span className="sr-only">Edit</span>
                                    </Link>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Edit Student</p>
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleResendWelcomeEmail(student.id)}
                                    disabled={sendingEmailTo === student.id}
                                    className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                                  >
                                    {sendingEmailTo === student.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Mail className="h-4 w-4" />
                                    )}
                                    <span className="sr-only">Resend Welcome Email</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Resend Welcome Email</p>
                                </TooltipContent>
                              </Tooltip>

                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleResetPassword(student.id)}
                                    disabled={resettingPasswordFor === student.id}
                                    className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700"
                                  >
                                    {resettingPasswordFor === student.id ? (
                                      <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                      <Key className="h-4 w-4" />
                                    )}
                                    <span className="sr-only">Reset Password</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Reset Password</p>
                                </TooltipContent>
                              </Tooltip>

                              {/* Access Control Actions */}
                              {student.accessRevoked ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRestoreAccess(student.id)}
                                      className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                                    >
                                      <ShieldCheck className="h-4 w-4" />
                                      <span className="sr-only">Restore Access</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Restore Access (After Fee Payment)</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : student.isActive !== false && !student.isArchived && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRevokeAccess(student.id)}
                                      className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700"
                                    >
                                      <ShieldOff className="h-4 w-4" />
                                      <span className="sr-only">Revoke Access</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Revoke Access (Fee Non-Payment)</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}

                              {/* Account Activation/Deactivation */}
                              {(student.isActive === false || student.status === 'INACTIVE') ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleReactivateStudent(student.id)}
                                      className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                                    >
                                      <UserCheck className="h-4 w-4" />
                                      <span className="sr-only">Reactivate</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Reactivate Student Account</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : !student.isArchived && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeactivateStudent(student.id)}
                                      className="h-8 w-8 p-0 text-orange-600 hover:text-orange-700"
                                    >
                                      <UserX className="h-4 w-4" />
                                      <span className="sr-only">Deactivate</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Deactivate Account (Student Leaves)</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}

                              {/* Archive Action */}
                              {!student.isArchived && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleArchiveStudent(student.id)}
                                      className="h-8 w-8 p-0 text-purple-600 hover:text-purple-700"
                                    >
                                      <Archive className="h-4 w-4" />
                                      <span className="sr-only">Archive</span>
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Archive (6 months after completion)</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}

                              {/* Delete - Disabled with warning */}
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteStudent()}
                                    className="h-8 w-8 p-0 text-gray-400 cursor-not-allowed"
                                    disabled
                                  >
                                    <Trash className="h-4 w-4" />
                                    <span className="sr-only">Delete</span>
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Delete Disabled - Use Revoke/Deactivate/Archive</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} students
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchStudents(currentPage - 1, pageSize)}
                  disabled={currentPage === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = Math.max(0, Math.min(totalPages - 5, currentPage - 2)) + i;
                    return (
                      <Button
                        key={pageNum}
                        variant={pageNum === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => fetchStudents(pageNum, pageSize)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum + 1}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchStudents(currentPage + 1, pageSize)}
                  disabled={currentPage >= totalPages - 1}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentsPage;
